import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useSessionAnalytics } from '../../hooks/useSessionAnalytics';
import { Calendar, TrendingUp } from 'lucide-react';

// Starburst gradient colors: blue, green, purple, and a bit of red
const STARBURST_COLORS = [
  { offset: '0%', stopColor: 'rgba(59, 130, 246, 1.0)', stopOpacity: 1 },   // Blue
  { offset: '33%', stopColor: 'rgba(34, 197, 94, 1.0)', stopOpacity: 1 },   // Green
  { offset: '66%', stopColor: 'rgba(168, 85, 247, 1.0)', stopOpacity: 1 }, // Purple
  { offset: '100%', stopColor: 'rgba(239, 68, 68, 0.8)', stopOpacity: 0.8 }, // Red (a bit)
];

interface SessionChartProps {
  className?: string;
}

export function SessionChart({ className = '' }: SessionChartProps) {
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day');
  const { data: analytics, isLoading, error } = useSessionAnalytics(period);

  // Format data for recharts
  const chartData = useMemo(() => {
    if (!analytics?.data) return [];
    
    return analytics.data.map((point) => {
      const date = new Date(point.date);
      let label: string;
      
      switch (period) {
        case 'day':
          label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          break;
        case 'week':
          label = `Week ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
          break;
        case 'month':
          label = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          break;
        default:
          label = point.date;
      }
      
      return {
        date: label,
        sessions: point.count,
        fullDate: point.date,
      };
    });
  }, [analytics, period]);

  if (error) {
    return (
      <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
        <CardContent className="p-6">
          <p className="text-rollops-accent text-sm">
            Error loading session analytics: {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`bg-[#221F1F] border-[#504A4A] ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-[#A9A3A3] flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Session Activity
          </CardTitle>
          {/* Period selector */}
          <div className="flex gap-2">
            {(['day', 'week', 'month'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  period === p
                    ? 'bg-[#9B1421] text-[#EBFFEE]'
                    : 'bg-[#363232] text-[#A9A3A3] hover:bg-[#504A4A]'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
            <span className="ml-3 text-[#A9A3A3]">Loading chart...</span>
          </div>
        ) : chartData.length > 0 ? (
          <div className="relative">
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="starburstGradient" x1="0" y1="0" x2="1" y2="0">
                    {STARBURST_COLORS.map((color, index) => (
                      <stop
                        key={index}
                        offset={color.offset}
                        stopColor={color.stopColor}
                        stopOpacity={color.stopOpacity}
                      />
                    ))}
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(169, 163, 163, 0.2)" />
                <XAxis
                  dataKey="date"
                  stroke="#A9A3A3"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke="#A9A3A3"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Sessions', angle: -90, position: 'insideLeft', fill: '#A9A3A3' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#221F1F',
                    border: '1px solid #504A4A',
                    borderRadius: '8px',
                    color: '#EBFFEE',
                  }}
                  labelStyle={{ color: '#A9A3A3' }}
                />
                <Line
                  type="monotone"
                  dataKey="sessions"
                  stroke="url(#starburstGradient)"
                  strokeWidth={3}
                  dot={{ fill: '#EBFFEE', r: 4 }}
                  activeDot={{ r: 6, fill: 'rgba(59, 130, 246, 0.8)' }}
                />
              </LineChart>
            </ResponsiveContainer>
            {analytics && (
              <div className="mt-4 flex items-center justify-between text-sm text-[#A9A3A3]">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>Total Sessions: {analytics.total_sessions.toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-4">
                  {/* Legend */}
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(59, 130, 246, 1.0)' }} />
                    <span className="text-xs">Blue</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 1.0)' }} />
                    <span className="text-xs">Green</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(168, 85, 247, 1.0)' }} />
                    <span className="text-xs">Purple</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(239, 68, 68, 0.8)' }} />
                    <span className="text-xs">Red</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-[#A9A3A3]">
            No session data available
          </div>
        )}
      </CardContent>
    </Card>
  );
}

